import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../utilities/cn';

interface BaseSkeletonProps {
  count?: number;
  animate?: boolean;
  className?: string;
}

interface TableSkeletonProps extends BaseSkeletonProps {
  variant: 'table';
  columnCount: number;
  rowClassName?: string;
  cellClassName?: string;
}

interface ListSkeletonProps
  extends BaseSkeletonProps,
    React.HTMLAttributes<HTMLUListElement> {
  variant: 'list';
  itemClassName?: string;
  showDot?: boolean;
  showMultipleLines?: boolean;
}

type SkeletonLoaderProps = TableSkeletonProps | ListSkeletonProps;

// Table skeleton component
const TableSkeleton: React.FC<TableSkeletonProps> = ({
  columnCount,
  count = 8,
  rowClassName,
  cellClassName,
  animate = true,
  className,
}) => {
  return (
    <>
      {Array.from({ length: count }).map((_, rowIndex) => (
        <tr
          key={`skeleton-row-${rowIndex}`}
          className={cn('border-b border-gray-200', rowClassName, className)}
        >
          {Array.from({ length: columnCount }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className={cn('px-4 py-3', cellClassName)}
            >
              <div
                className={cn(
                  'h-4 w-full rounded bg-gray-200',
                  animate && 'animate-pulse',
                )}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

// List skeleton component (for activity items)
const ListSkeleton: React.FC<ListSkeletonProps> = ({
  count = 6,
  itemClassName,
  showDot = true,
  showMultipleLines = true,
  animate = true,
  className,
  ...rest
}) => {
  return (
    <ul className={cn('space-y-2', className)} {...rest}>
      {Array.from({ length: count }).map((_, index) => (
        <motion.li
          key={`skeleton-item-${index}`}
          className={cn('flex items-start space-x-3', itemClassName)}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{
            delay: 0.6 + index * 0.1,
            duration: 1.2,
            repeat: Infinity,
            repeatDelay: 0.5,
            repeatType: 'loop',
            ease: 'easeInOut',
          }}
        >
          {showDot && (
            <div
              className={cn(
                'mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-gray-300',
                animate && 'animate-pulse',
              )}
              aria-hidden="true"
            />
          )}
          <div className="min-w-0 flex-1 space-y-2">
            {showMultipleLines ? (
              <>
                <div
                  className={cn(
                    'h-4 w-3/4 rounded bg-gray-200',
                    animate && 'animate-pulse',
                  )}
                />
                <div
                  className={cn(
                    'h-3 w-1/2 rounded bg-gray-200',
                    animate && 'animate-pulse',
                  )}
                />
                <div
                  className={cn(
                    'h-3 w-2/3 rounded bg-gray-200',
                    animate && 'animate-pulse',
                  )}
                />
              </>
            ) : (
              <div
                className={cn(
                  'h-4 w-full rounded bg-gray-200',
                  animate && 'animate-pulse',
                )}
              />
            )}
          </div>
        </motion.li>
      ))}
    </ul>
  );
};

// Main skeleton loader component
export const SkeletonLoader: React.FC<SkeletonLoaderProps> = (props) => {
  if (props.variant === 'table') {
    return <TableSkeleton {...props} />;
  }
  return <ListSkeleton {...props} />;
};

// Legacy export for backward compatibility
export const SkeletonTableRows: React.FC<
  Omit<TableSkeletonProps, 'variant'>
> = (props) => {
  return <TableSkeleton variant="table" {...props} />;
};

export default SkeletonLoader;
